import React, { useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  fulfillmentUpdateFeeSchema,
  FulfillmentUpdateFeeFormValues,
} from "@/types/fulfillment-type";
import { updateFulfillmentCost } from "@/api/fulfillment-api";
import { useToast } from "@/hooks/use-toast";
import { Fulfillment } from "@/types/fulfillment-type";

interface UpdateDeliveryFeeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fulfillment?: Fulfillment | null;
  onSuccess?: () => void;
}

export function UpdateDeliveryFeeDialog({
  open,
  onOpenChange,
  fulfillment,
  onSuccess,
}: UpdateDeliveryFeeDialogProps) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = React.useState(false);
  const [inputValue, setInputValue] = React.useState<string>("");

  const form = useForm<FulfillmentUpdateFeeFormValues>({
    resolver: zodResolver(fulfillmentUpdateFeeSchema),
    defaultValues: {
      price: 0,
    },
  });

  // Reset form when fulfillment changes or dialog opens
  useEffect(() => {
    if (fulfillment && open) {
      // fulfillment.price is stored in dollars (as string), parse directly
      const currentPrice = fulfillment.price
        ? parseFloat(fulfillment.price)
        : 0;
      form.reset({
        price: currentPrice,
      });
      setInputValue(currentPrice === 0 ? "" : currentPrice.toString());
    }
  }, [fulfillment, open, form]);

  const handleSubmit = async (values: FulfillmentUpdateFeeFormValues) => {
    if (!fulfillment?.id) {
      toast({
        title: "Error",
        description: "Fulfillment ID is missing",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      // Ensure empty or invalid input value sends 0 to payload
      const priceToSend =
        inputValue === "" || inputValue === "-" || inputValue === "."
          ? 0
          : values.price ?? 0;

      const response = await updateFulfillmentCost(fulfillment.id, {
        price: priceToSend,
      });

      if (response.success) {
        toast({
          title: "Success",
          description: "Delivery fee updated successfully",
        });
        onOpenChange(false);
        onSuccess?.();
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to update delivery fee",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error("Error updating delivery fee:", error);
      toast({
        title: "Error",
        description:
          error?.message || "An error occurred while updating delivery fee",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Update Delivery Fee</DialogTitle>
          <DialogDescription>
            Update the delivery fee for this fulfillment
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="price"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Delivery Fee (USD)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      placeholder="0.00"
                      value={inputValue}
                      onChange={(e) => {
                        const raw = e.target.value;
                        setInputValue(raw);

                        // Allow empty string or intermediate states while typing
                        if (raw === "" || raw === "-" || raw === ".") {
                          field.onChange(0);
                          return;
                        }

                        // Parse the value - Number() handles "0.", "0.1", "0.01", etc. correctly
                        const parsed = Number(raw);
                        if (!Number.isNaN(parsed)) {
                          field.onChange(parsed);
                        }
                      }}
                      onBlur={(e) => {
                        const raw = e.target.value.trim();

                        // Convert empty or invalid values to 0 on blur
                        if (raw === "" || raw === "-" || raw === ".") {
                          setInputValue("");
                          field.onChange(0);
                        } else {
                          const parsed = Number(raw);
                          if (!Number.isNaN(parsed) && isFinite(parsed)) {
                            // Update form value and display
                            field.onChange(parsed);
                            setInputValue(parsed.toString());
                          } else {
                            // Invalid input, reset to empty
                            setInputValue("");
                            field.onChange(0);
                          }
                        }
                        field.onBlur();
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading || form.formState.isSubmitting}
              >
                {isLoading || form.formState.isSubmitting ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Updating...
                  </div>
                ) : (
                  "Update Fee"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
